/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import Fuse from 'fuse.js';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Project } from '@/commons/protocols';
import DisplayError from '@/components/atoms/Error';
import SearchBar from '@/components/atoms/SearchBar';
import { Button } from '@/components/ui/button';
import { useGet } from '@/net/commons';
import { formatDate } from '@/utils';

type CardProps = { project?: Project; isLoading: boolean };

const Card = ({ project, isLoading }: CardProps) => {
    return (
        <a
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-100"
            href={project?.slug && `/project/${project.slug}`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div className="flex flex-col">
                        {isLoading || project === undefined ? (
                            <>
                                <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4" />
                                <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4" />
                                <span className="sr-only">Loading...</span>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg font-medium text-gray-900 dark:text-white ml-3">
                                    {project.name}
                                </h2>
                                <p className="text-gray-500 text-xs ml-3 sm:whitespace-nowrap">
                                    created: {formatDate(project.createdAt)}
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </a>
    );
};

const Projects = () => {
    const { data, isLoading, error }: { data: Project[] | undefined; isLoading: boolean; error: any } =
        useGet(`/bff/projects`);

    const [filteredData, setFilteredData] = useState<Project[]>([]);
    const [fuse, setFuse] = useState<Fuse<Project> | undefined>(undefined);

    const fuseOptions = {
        keys: ['name'],
    };

    useEffect(() => {
        if (data) {
            setFilteredData(data);
            setFuse(new Fuse(data, fuseOptions));
        }
    }, [data, isLoading]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.target?.value === '') {
            data && setFilteredData(data);
        } else if (fuse) {
            const searchResults = fuse.search(event?.target?.value);
            searchResults && setFilteredData(searchResults.map((e) => e.item));
        }
    };

    return (
        <div className="mt-6 sm:w-10/12 md:w-8/12 mx-auto">
            <div role="toolbar" className="flex mb-4 justify-between">
                <SearchBar className="flex-1 mr-12" onChange={handleSearch} />
                <Button className="mx-auto whitespace-nowrap" asChild>
                    <Link to={`/project/create`}>Create project</Link>
                </Button>
            </div>
            {isLoading && !data ? (
                <Card isLoading />
            ) : error ? (
                <DisplayError error={error} />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6 overflow-x-hidden">
                    {filteredData.map((project) => (
                        <Card key={project.name} project={project} isLoading={isLoading} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default Projects;
